import type { UIEvent } from "react";
import { useEffect, useMemo, useState } from "react";

type UseSearchableInfiniteListProps<T> = {
	items: T[];
	getSearchableFields: (item: T) => (string | null | undefined)[];
	pageSize?: number;
	scrollThreshold?: number;
};

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SCROLL_THRESHOLD = 2;

/** Client-side search + "reveal more on scroll" pagination shared by mobile address card lists. */
export function useSearchableInfiniteList<T>({
	items,
	getSearchableFields,
	pageSize = DEFAULT_PAGE_SIZE,
	scrollThreshold = DEFAULT_SCROLL_THRESHOLD,
}: UseSearchableInfiniteListProps<T>) {
	const [searchTerm, setSearchTerm] = useState("");
	const [visibleCount, setVisibleCount] = useState(pageSize);

	const filteredItems = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		if (!term) return items;
		return items.filter((item) =>
			getSearchableFields(item)
				.filter(Boolean)
				.some((field) => field?.toLowerCase().includes(term)),
		);
	}, [searchTerm, items, getSearchableFields]);

	useEffect(() => {
		setVisibleCount(pageSize);
	}, [searchTerm, pageSize]);

	const visibleItems = useMemo(
		() => filteredItems.slice(0, Math.min(visibleCount, filteredItems.length)),
		[filteredItems, visibleCount],
	);

	const handleScroll = (event: UIEvent<HTMLDivElement>) => {
		if (visibleCount >= filteredItems.length) return;
		const target = event.currentTarget;
		const distanceFromBottom =
			target.scrollHeight - (target.scrollTop + target.clientHeight);
		if (distanceFromBottom <= scrollThreshold) {
			setVisibleCount((count) =>
				Math.min(count + pageSize, filteredItems.length),
			);
		}
	};

	return {
		searchTerm,
		setSearchTerm,
		visibleItems,
		filteredCount: filteredItems.length,
		handleScroll,
	};
}
